import { DistanceUnit, LengthUnit, WeightUnit } from '@data-access';

export const UnitCategory = { Distance: 'DISTANCE', Length: 'LENGTH', Weight: 'WEIGHT' } as const;
export type UnitCategory = (typeof UnitCategory)[keyof typeof UnitCategory];
export type UnitDisplay = 'short' | 'long';
export type AnyUnit = DistanceUnit | LengthUnit | WeightUnit;

const KM_PER_MILE = 1.609344;
const CM_PER_INCH = 2.54;
const KG_PER_POUND = 0.45359237;
const ROUNDING_FACTOR = 100;

interface UnitDef {
  category: UnitCategory;
  /** base - imperial system (mi, in, lbs) */
  toBase: number;
}

const UNIT_DEFS: Record<AnyUnit, UnitDef> = {
  // DISTANCE, base – MI
  [DistanceUnit.Mi]: { category: UnitCategory.Distance, toBase: 1 },
  [DistanceUnit.Km]: { category: UnitCategory.Distance, toBase: 1 / KM_PER_MILE },

  // LENGTH, base – IN
  [LengthUnit.In]: { category: UnitCategory.Length, toBase: 1 },
  [LengthUnit.Cm]: { category: UnitCategory.Length, toBase: 1 / CM_PER_INCH },

  // WEIGHT, base – LB
  [WeightUnit.Lb]: { category: UnitCategory.Weight, toBase: 1 },
  [WeightUnit.Kg]: { category: UnitCategory.Weight, toBase: 1 / KG_PER_POUND },
};

export function convertUnitRaw(value: number, from: AnyUnit, to: AnyUnit): number {
  if (from === to) {
    return value;
  }

  const fromDef = UNIT_DEFS[from];
  const toDef = UNIT_DEFS[to];

  if (fromDef.category !== toDef.category) {
    throw new Error(`Cannot convert ${from} (${fromDef.category}) to ${to} (${toDef.category})`);
  }
  return (value * fromDef.toBase) / toDef.toBase;
}

export function convertUnit(value: number, from: AnyUnit, to: AnyUnit): number {
  return round2(convertUnitRaw(value, from, to));
}

function round2(v: number): number {
  return Math.round((v + Number.EPSILON) * ROUNDING_FACTOR) / ROUNDING_FACTOR;
}

/**
 * '20.5' → 20.5, 20.5 → 20.5
 * null / undefined / '' / '  ' → 0
 * 'abc' / NaN / Infinity → 0
 */
export function toNumber(value: number | string | null | undefined): number {
  if (value == null) return 0; // null + undefined
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }
  const trimmed = value.trim();
  if (trimmed === '') return 0;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : 0;
}
